import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Typography,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { dataEntryAPI, stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface FuelPrice {
  _id: string;
  station: {
    _id: string;
    name: string;
  };
  fuelType: string;
  price: number;
  unit: string;
  effectiveDate: string;
  isActive: boolean;
}

const FuelPrices: React.FC = () => {
  const [prices, setPrices] = useState<FuelPrice[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPrice, setEditingPrice] = useState<FuelPrice | null>(null);
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchPrices();
  }, [selectedStation]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const stationId = selectedStation === 'all' ? undefined : selectedStation;
      const response = await dataEntryAPI.getFuelPrices(stationId);
      setPrices(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fiyatlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrice = () => {
    setEditingPrice(null);
    form.resetFields();
    form.setFieldsValue({
      effectiveDate: dayjs(),
      unit: 'litre'
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.station,
        fuelType: values.fuelType,
        price: values.price,
        unit: values.unit,
        effectiveDate: values.effectiveDate?.toISOString()
      };

      await dataEntryAPI.addFuelPrice(data);
      message.success('Yakıt fiyatı başarıyla eklendi');
      setModalVisible(false);
      form.resetFields();
      fetchPrices();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const fuelTypeLabels: any = {
    benzin: 'Benzin',
    diesel: 'Dizel',
    lpg: 'LPG',
    premium_benzin: 'Premium Benzin',
    premium_diesel: 'Premium Dizel'
  };

  const columns = [
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
    },
    {
      title: 'Yakıt Tipi',
      dataIndex: 'fuelType',
      key: 'fuelType',
      render: (type: string) => (
        <Tag color="blue">{fuelTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: FuelPrice) => (
        <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#52c41a' }}>
          {price.toLocaleString('tr-TR')} ₺ / {record.unit}
        </span>
      ),
    },
    {
      title: 'Geçerlilik Tarihi',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (date: string) => dayjs(date).format('DD MMMM YYYY'),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
  ];

  // İstasyon bazlı fiyat özeti
  const stationSummary = stations.map(station => {
    const stationPrices = prices.filter(p => p.station._id === station._id && p.isActive);
    return {
      station: station.name,
      count: stationPrices.length,
      avgPrice: stationPrices.length > 0
        ? stationPrices.reduce((sum, p) => sum + p.price, 0) / stationPrices.length
        : 0
    };
  });

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <DollarOutlined /> Yakıt Fiyat Yönetimi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Select
              value={selectedStation}
              onChange={setSelectedStation}
              style={{ width: '100%', maxWidth: 200 }}
              placeholder="İstasyon seçin"
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPrice} block={isMobile}>
              Yeni Fiyat Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Aktif Fiyat"
              value={prices.filter(p => p.isActive).length}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Fiyat"
              value={prices.filter(p => p.isActive).length > 0
                ? prices.filter(p => p.isActive).reduce((sum, p) => sum + p.price, 0) / prices.filter(p => p.isActive).length
                : 0}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="İstasyon Sayısı"
              value={new Set(prices.map(p => p.station._id)).size}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Yakıt Tipi Sayısı"
              value={new Set(prices.map(p => p.fuelType)).size}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={prices}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      <Modal
        title={editingPrice ? 'Fiyat Düzenle' : 'Yeni Yakıt Fiyatı Ekle'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="fuelType"
            label="Yakıt Tipi"
            rules={[{ required: true, message: 'Yakıt tipi seçiniz' }]}
          >
            <Select placeholder="Yakıt tipi seçin">
              <Option value="benzin">Benzin</Option>
              <Option value="diesel">Dizel</Option>
              <Option value="lpg">LPG</Option>
              <Option value="premium_benzin">Premium Benzin</Option>
              <Option value="premium_diesel">Premium Dizel</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="price"
                label="Fiyat (₺)"
                rules={[{ required: true, message: 'Fiyat giriniz' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Fiyat"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unit"
                label="Birim"
                rules={[{ required: true, message: 'Birim seçiniz' }]}
              >
                <Select>
                  <Option value="litre">Litre</Option>
                  <Option value="kg">Kg</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="effectiveDate"
            label="Geçerlilik Tarihi"
            rules={[{ required: true, message: 'Tarih seçiniz' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPrice ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FuelPrices;

